<template>
  <div>
    <!-- Cabeçalho Principal -->
    <b-row class="mb-2">
      <b-col cols="12">
        <b-card class="bg-gradient-primary text-white py-1">
          <b-row align-v="center">
            <b-col md="8">
              <h1 class="text-white mb-25 d-flex align-items-center">
                <feather-icon icon="FileTextIcon" size="28" class="mr-1" />
                Relatórios do Sistema
              </h1>
              <p class="text-white mb-0" style="opacity: 0.9;">
                Gere e exporte relatórios consolidados de desempenho, pontuação e engajamento dos estudantes.
              </p>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Card de Seleção e Filtros -->
    <b-card no-body class="mb-2">
      <b-card-header class="pb-50">
        <h4 class="card-title mb-0 d-flex align-items-center">
          <feather-icon icon="FilterIcon" size="18" class="mr-50 text-primary" />
          Configurações do Relatório
        </h4>
      </b-card-header>
      <b-card-body class="pt-1">
        <b-row>
          <!-- 1. Tipo do Relatório -->
          <b-col md="6" class="mb-1">
            <b-form-group label="Tipo de Relatório" label-for="tipo-relatorio" label-class="font-weight-bold">
              <v-select
                id="tipo-relatorio"
                v-model="tipoSelecionado"
                :options="tiposRelatorio"
                label="nome"
                :reduce="tipo => tipo.id"
                :clearable="false"
                placeholder="Selecione o tipo de relatório"
              >
                <template #option="option">
                  <div>
                    <strong>{{ option.nome }}</strong>
                    <br />
                    <small class="text-muted">{{ option.descricao }}</small>
                  </div>
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <!-- 2. Escola -->
          <b-col md="6" class="mb-1">
            <b-form-group label="Escola" label-for="select-escola" label-class="font-weight-bold">
              <v-select
                id="select-escola"
                v-model="escolaSelecionada"
                :options="escolas"
                label="name"
                :reduce="escola => escola"
                :loading="loadingEscolas"
                placeholder="Selecione a escola para gerar o relatório"
              >
                <template #no-options>
                  Nenhuma escola encontrada
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-50">
          <b-col cols="12" class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="d-flex align-items-center mb-1 mb-md-0">
              <b-button
                variant="primary"
                class="mr-1 d-flex align-items-center"
                :disabled="!escolaSelecionada || loadingGerando"
                @click="gerarRelatorio"
              >
                <b-spinner v-if="loadingGerando" small class="mr-50" />
                <feather-icon v-else icon="PlayIcon" size="16" class="mr-50" />
                {{ loadingGerando ? 'Gerando Relatório...' : 'Gerar Relatório' }}
              </b-button>

              <b-button
                v-if="dadosRelatorio.length > 0"
                variant="outline-success"
                class="d-flex align-items-center"
                @click="baixarExcel"
              >
                <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
                Baixar Planilha (XLS)
              </b-button>
            </div>

            <span v-if="relatorioGerado && escolaSelecionada" class="text-muted small">
              Escola selecionada: <strong>{{ escolaSelecionada.name }}</strong>
            </span>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Cards de Resumo e Métricas (Visíveis após gerar) -->
    <b-row v-if="relatorioGerado && dadosRelatorio.length > 0" class="mb-2">
      <b-col sm="6" lg="3" class="mb-1 mb-lg-0">
        <b-card class="text-center shadow-none border mb-0">
          <div class="avatar bg-light-primary p-50 mb-1 mx-auto">
            <feather-icon icon="UsersIcon" size="24" class="text-primary" />
          </div>
          <h3 class="font-weight-bolder mb-25">{{ dadosRelatorio.length }}</h3>
          <p class="card-text text-muted mb-0">Estudantes no Relatório</p>
        </b-card>
      </b-col>

      <b-col sm="6" lg="3" class="mb-1 mb-lg-0">
        <b-card class="text-center shadow-none border mb-0">
          <div class="avatar bg-light-success p-50 mb-1 mx-auto">
            <feather-icon icon="CheckCircleIcon" size="24" class="text-success" />
          </div>
          <h3 class="font-weight-bolder mb-25">{{ totalConcluiramQuatroMais }}</h3>
          <p class="card-text text-muted mb-0">Concluíram 4+ Desafios</p>
        </b-card>
      </b-col>

      <b-col sm="6" lg="3" class="mb-1 mb-lg-0">
        <b-card class="text-center shadow-none border mb-0">
          <div class="avatar bg-light-info p-50 mb-1 mx-auto">
            <feather-icon icon="ShieldIcon" size="24" class="text-info" />
          </div>
          <h3 class="font-weight-bolder mb-25">{{ totalSolucoesPatrulha }}</h3>
          <p class="card-text text-muted mb-0">Soluções Patrulha Aprovadas</p>
        </b-card>
      </b-col>

      <b-col sm="6" lg="3">
        <b-card class="text-center shadow-none border mb-0">
          <div class="avatar bg-light-warning p-50 mb-1 mx-auto">
            <feather-icon icon="ActivityIcon" size="24" class="text-warning" />
          </div>
          <h3 class="font-weight-bolder mb-25">{{ totalAcessosApp }}</h3>
          <p class="card-text text-muted mb-0">Acessos Totais ao App</p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tabela de Dados do Relatório -->
    <b-card v-if="relatorioGerado" no-body>
      <b-card-header class="d-flex flex-wrap justify-content-between align-items-center py-1">
        <div>
          <h4 class="card-title mb-25">Prévia dos Dados</h4>
          <span class="text-muted small">Exibindo registros gerados para exportação</span>
        </div>

        <div class="d-flex align-items-center mt-1 mt-md-0">
          <!-- Campo de Busca Rápida -->
          <b-input-group class="input-group-merge mr-1" style="min-width: 260px;">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="filtroBusca"
              placeholder="Filtrar por estudante ou turma..."
            />
          </b-input-group>

          <b-button
            variant="success"
            class="d-flex align-items-center"
            @click="baixarExcel"
          >
            <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
            Baixar XLS
          </b-button>
        </div>
      </b-card-header>

      <b-table
        :items="dadosFiltrados"
        :fields="colunasTabela"
        responsive
        striped
        hover
        show-empty
        empty-text="Nenhum estudante encontrado para esta escola."
        class="position-relative mb-0"
        :current-page="paginaAtual"
        :per-page="itensPorPagina"
      >
        <!-- Formatação Código/ID Estudante -->
        <template #cell(codigoEstudante)="data">
          <span class="font-weight-bold text-primary">{{ data.value }}</span>
        </template>

        <!-- Formatação Turma -->
        <template #cell(turma)="data">
          <b-badge variant="light-secondary">{{ data.value }}</b-badge>
        </template>

        <!-- Patrulha Pré e Pós -->
        <template #cell(patrulhaPre)="data">
          <span>{{ data.value !== null ? data.value + ' pts' : '-' }}</span>
        </template>
        <template #cell(patrulhaPos)="data">
          <span>{{ data.value !== null ? data.value + ' pts' : '-' }}</span>
        </template>

        <!-- Patrulha Resultado -->
        <template #cell(patrulhaResultado)="data">
          <b-badge :variant="resolverVariacaoBadge(data.value)">
            {{ data.value }}
          </b-badge>
        </template>

        <!-- Janela Pré e Pós -->
        <template #cell(janelaPre)="data">
          <span>{{ data.value !== null ? data.value + ' pts' : '-' }}</span>
        </template>
        <template #cell(janelaPos)="data">
          <span>{{ data.value !== null ? data.value + ' pts' : '-' }}</span>
        </template>

        <!-- Janela Resultado -->
        <template #cell(janelaResultado)="data">
          <b-badge :variant="resolverVariacaoBadge(data.value)">
            {{ data.value }}
          </b-badge>
        </template>

        <!-- Desafios Concluídos: Sim/Não Badges -->
        <template #cell(concluiuQuiz)="data">
          <b-badge :variant="data.value === 'Sim' ? 'light-success' : 'light-secondary'">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(concluiuPatrulha)="data">
          <b-badge :variant="data.value === 'Sim' ? 'light-success' : 'light-secondary'">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(concluiuJanela)="data">
          <b-badge :variant="data.value === 'Sim' ? 'light-success' : 'light-secondary'">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(concluiuColeta)="data">
          <b-badge :variant="data.value === 'Sim' ? 'light-success' : 'light-secondary'">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(concluiuArboturma)="data">
          <b-badge :variant="data.value === 'Sim' ? 'light-success' : 'light-secondary'">
            {{ data.value }}
          </b-badge>
        </template>

        <!-- Concluiu 4 ou Mais -->
        <template #cell(concluiuQuatroOuMais)="data">
          <b-badge :variant="data.value === 'Sim' ? 'success' : 'secondary'">
            {{ data.value }}
          </b-badge>
        </template>
      </b-table>

      <!-- Paginação -->
      <b-card-footer class="d-flex justify-content-between align-items-center py-1">
        <span class="text-muted small">
          Mostrando {{ inicioRegistro }} até {{ fimRegistro }} de {{ dadosFiltrados.length }} registros
        </span>

        <b-pagination
          v-model="paginaAtual"
          :total-rows="dadosFiltrados.length"
          :per-page="itensPorPagina"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          class="mb-0"
        />
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardFooter,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BButton,
  BTable,
  BPagination,
  BBadge,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import schoolService from '@/services/schoolService'
import relatoriosService from '@/services/relatoriosService'

export default {
  name: 'Relatorios',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardFooter,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BButton,
    BTable,
    BPagination,
    BBadge,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      tiposRelatorio: [
        {
          id: 'pontuacao_alunos',
          nome: 'Relatório de Pontuação e Desempenho dos Alunos',
          descricao: 'Pré/pós testes, variações de notas, soluções aprovadas e status de desafios por estudante.',
        },
      ],
      tipoSelecionado: 'pontuacao_alunos',

      escolas: [],
      escolaSelecionada: null,
      loadingEscolas: false,

      loadingGerando: false,
      relatorioGerado: false,
      dadosRelatorio: [],

      filtroBusca: '',
      paginaAtual: 1,
      itensPorPagina: 15,

      colunasTabela: [
        { key: 'codigoEstudante', label: 'Código/ID', sortable: true },
        { key: 'turma', label: 'Turma', sortable: true },
        { key: 'patrulhaPre', label: 'Patrulha Pré', sortable: true },
        { key: 'patrulhaPos', label: 'Patrulha Pós', sortable: true },
        { key: 'patrulhaVariacao', label: 'Variação Pat.', sortable: true },
        { key: 'patrulhaResultado', label: 'Resultado Pat.', sortable: true },
        { key: 'janelaPre', label: 'Janela Pré', sortable: true },
        { key: 'janelaPos', label: 'Janela Pós', sortable: true },
        { key: 'janelaVariacao', label: 'Variação Jan.', sortable: true },
        { key: 'janelaResultado', label: 'Resultado Jan.', sortable: true },
        { key: 'solucoesAprovadasPatrulha', label: 'Soluções Pat.', sortable: true },
        { key: 'solucoesAprovadasJanela', label: 'Soluções Jan.', sortable: true },
        { key: 'concluiuQuiz', label: 'QuizArbo', sortable: true },
        { key: 'concluiuPatrulha', label: 'Patrulha', sortable: true },
        { key: 'concluiuJanela', label: 'Janelas', sortable: true },
        { key: 'concluiuColeta', label: 'Coleta', sortable: true },
        { key: 'concluiuArboturma', label: 'ArboTurma', sortable: true },
        { key: 'totalDesafiosConcluidos', label: 'Total Desafios', sortable: true },
        { key: 'concluiuQuatroOuMais', label: 'Concluiu 4+', sortable: true },
        { key: 'numeroAcessosApp', label: 'Acessos App', sortable: true },
      ],
    }
  },
  computed: {
    dadosFiltrados() {
      if (!this.filtroBusca) return this.dadosRelatorio

      const busca = this.filtroBusca.toLowerCase().trim()
      return this.dadosRelatorio.filter(
        item =>
          (item.codigoEstudante && item.codigoEstudante.toLowerCase().includes(busca)) ||
          (item.turma && item.turma.toLowerCase().includes(busca)),
      )
    },
    inicioRegistro() {
      if (this.dadosFiltrados.length === 0) return 0
      return (this.paginaAtual - 1) * this.itensPorPagina + 1
    },
    fimRegistro() {
      const fim = this.paginaAtual * this.itensPorPagina
      return fim > this.dadosFiltrados.length ? this.dadosFiltrados.length : fim
    },
    totalConcluiramQuatroMais() {
      return this.dadosRelatorio.filter(x => x.concluiuQuatroOuMais === 'Sim').length
    },
    totalSolucoesPatrulha() {
      return this.dadosRelatorio.reduce((acc, curr) => acc + (curr.solucoesAprovadasPatrulha || 0), 0)
    },
    totalAcessosApp() {
      return this.dadosRelatorio.reduce((acc, curr) => acc + (curr.numeroAcessosApp || 0), 0)
    },
  },
  async created() {
    await this.carregarEscolas()
  },
  methods: {
    async carregarEscolas() {
      this.loadingEscolas = true
      try {
        const response = await schoolService.getAll({ pageSize: 1000 })
        this.escolas = response.content || response || []
      } catch (error) {
        console.error('Erro ao carregar escolas:', error)
      } finally {
        this.loadingEscolas = false
      }
    },

    async gerarRelatorio() {
      if (!this.escolaSelecionada || !this.escolaSelecionada.id) return

      this.loadingGerando = true
      try {
        const dados = await relatoriosService.getPontuacaoAlunos(this.escolaSelecionada.id)
        this.dadosRelatorio = dados || []
        this.relatorioGerado = true
        this.paginaAtual = 1
      } catch (error) {
        console.error('Erro ao gerar relatório:', error)
      } finally {
        this.loadingGerando = false
      }
    },

    resolverVariacaoBadge(resultado) {
      if (resultado === 'Melhorou') return 'light-success'
      if (resultado === 'Piorou') return 'light-danger'
      if (resultado === 'Manteve') return 'light-info'
      return 'light-secondary'
    },

    baixarExcel() {
      if (!this.dadosRelatorio || this.dadosRelatorio.length === 0) return

      const nomeEscola = this.escolaSelecionada ? this.escolaSelecionada.name : 'Escola'
      const dataHora = new Date().toISOString().slice(0, 10)
      const nomeArquivo = `Relatorio_Pontuacao_${nomeEscola.replace(/[^a-zA-Z0-9]/g, '_')}_${dataHora}.xls`

      // Geração de planilha formatada em HTML/XML compatível com Excel (.xls)
      const headers = [
        'Código/ID do estudante',
        'Turma',
        'Patrulha em Foco — pré-teste (pontos)',
        'Patrulha em Foco — pós-teste (pontos)',
        'Patrulha em Foco — variação (pós − pré)',
        'Patrulha em Foco — resultado',
        'Janelas e Tanques — pré-teste (pontos)',
        'Janelas e Tanques — pós-teste (pontos)',
        'Janelas e Tanques — variação (pós − pré)',
        'Janelas e Tanques — resultado',
        'Soluções aprovadas — Patrulha em Foco (quantidade)',
        'Soluções aprovadas — Janelas e Tanques (quantidade)',
        'Concluiu QuizArbo? (Sim/Não)',
        'Concluiu Patrulha em Foco? (Sim/Não)',
        'Concluiu Janelas e Tanques? (Sim/Não)',
        'Concluiu Coleta & Reciclagem? (Sim/Não)',
        'Concluiu ArboTurma? (Sim/Não)',
        'Total de desafios concluídos',
        'Concluiu 4 ou mais desafios? (Sim/Não)',
        'Número de acessos ao aplicativo',
      ]

      let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">'
      html += '<head><meta charset="utf-8" /><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Pontuação de Alunos</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>'
      html += '<body><table border="1">'

      // Cabeçalho
      html += '<tr style="background-color: #258b70; color: #ffffff; font-weight: bold;">'
      headers.forEach(h => {
        html += `<th style="padding: 8px;">${h}</th>`
      })
      html += '</tr>'

      // Linhas de dados
      this.dadosRelatorio.forEach(item => {
        html += '<tr>'
        html += `<td>${item.codigoEstudante || ''}</td>`
        html += `<td>${item.turma || ''}</td>`
        html += `<td>${item.patrulhaPre !== null && item.patrulhaPre !== undefined ? item.patrulhaPre : ''}</td>`
        html += `<td>${item.patrulhaPos !== null && item.patrulhaPos !== undefined ? item.patrulhaPos : ''}</td>`
        html += `<td>${item.patrulhaVariacao !== null && item.patrulhaVariacao !== undefined ? item.patrulhaVariacao : ''}</td>`
        html += `<td>${item.patrulhaResultado || ''}</td>`
        html += `<td>${item.janelaPre !== null && item.janelaPre !== undefined ? item.janelaPre : ''}</td>`
        html += `<td>${item.janelaPos !== null && item.janelaPos !== undefined ? item.janelaPos : ''}</td>`
        html += `<td>${item.janelaVariacao !== null && item.janelaVariacao !== undefined ? item.janelaVariacao : ''}</td>`
        html += `<td>${item.janelaResultado || ''}</td>`
        html += `<td>${item.solucoesAprovadasPatrulha || 0}</td>`
        html += `<td>${item.solucoesAprovadasJanela || 0}</td>`
        html += `<td>${item.concluiuQuiz || 'Não'}</td>`
        html += `<td>${item.concluiuPatrulha || 'Não'}</td>`
        html += `<td>${item.concluiuJanela || 'Não'}</td>`
        html += `<td>${item.concluiuColeta || 'Não'}</td>`
        html += `<td>${item.concluiuArboturma || 'Não'}</td>`
        html += `<td>${item.totalDesafiosConcluidos || 0}</td>`
        html += `<td>${item.concluiuQuatroOuMais || 'Não'}</td>`
        html += `<td>${item.numeroAcessosApp || 0}</td>`
        html += '</tr>'
      })

      html += '</table></body></html>'

      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = nomeArquivo
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.table-responsive {
  max-height: 600px;
}
</style>
